import React from "react";
import {
  Card,
  Descriptions,
  Empty,
  Avatar,
  Typography,
  Button,
  Divider,
  Tag,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  IdcardOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  ProfileOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { CLIENT_TYPES, CLIENT_TYPES_COLORS } from "../../utils/constants";

const { Title, Text } = Typography;

const getOwnerDisplayName = (owner) => {
  if (!owner) return "N/A";
  if (owner.type === "individuals") {
    return (
      `${owner.civility || ""} ${owner.first_name || ""} ${
        owner.last_name || ""
      }`.trim() || "N/A"
    );
  }
  return owner.name || "N/A";
};

const VehicleOwnerTab = ({ owner }) => {
  if (!owner) {
    return (
      <div className="p-4">
        <Card className="shadow-md rounded-lg">
          <Empty
            description={
              <span className="text-gray-600 text-lg">
                Aucune information sur le propriétaire
              </span>
            }
            className="my-12"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          ></Empty>
        </Card>
      </div>
    );
  }

  const displayName = getOwnerDisplayName(owner);

  return (
    <div className="p-4">
      <Card
        className="shadow-md hover:shadow-lg transition-all duration-300 border-0"
        bordered={false}
      >
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div className="flex items-center mb-4 md:mb-0">
            <Avatar
              size={80}
              icon={<UserOutlined />}
              className="bg-gradient-to-r from-green-500 to-teal-600 mr-5 shadow-md text-xl"
            />
            <div>
              <Title level={2} className="m-0 text-green-800">
                {displayName}
              </Title>
              <div className="flex items-center text-gray-500 mt-1">
                <Tag color={CLIENT_TYPES_COLORS[owner.type] || "default"}>
                  {CLIENT_TYPES[owner.type] || owner.type}
                </Tag>
              </div>
            </div>
          </div>

          <Link to={`/client/${owner.id}`}>
            <Button
              type="primary"
              icon={<ProfileOutlined />}
              className="bg-green-600 hover:bg-green-700 border-0 shadow-md"
            >
              Voir fiche client
            </Button>
          </Link>
        </div>

        <Divider className="my-6" />

        <div className="bg-gray-50 p-6 rounded-lg shadow-inner mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center mb-3">
                <IdcardOutlined className="text-xl text-blue-600 mr-2" />
                <Text strong className="text-lg">
                  Identité
                </Text>
              </div>
              {owner.type === "individuals" && (
                <>
                  <p>
                    <strong>Nom:</strong> {owner.last_name || "N/A"}
                  </p>
                  <p>
                    <strong>Prénom:</strong> {owner.first_name || "N/A"}
                  </p>
                  <p>
                    <strong>Civilité:</strong> {owner.civility || "N/A"}
                  </p>
                  <p>
                    <strong>CIN:</strong> {owner.cin || "N/A"}
                  </p>
                </>
              )}
              {owner.type === "companies" && (
                <>
                  <p>
                    <strong>Raison Sociale:</strong> {owner.name || "N/A"}
                  </p>
                  <p>
                    <strong>ICE:</strong> {owner.ice || "N/A"}
                  </p>
                  <p>
                    <strong>RC:</strong> {owner.rc || "N/A"}
                  </p>
                </>
              )}
              {owner.type === "institutions" && (
                <>
                  <p>
                    <strong>Nom Institution:</strong> {owner.name || "N/A"}
                  </p>
                  <p>
                    <strong>Organisme public:</strong>{" "}
                    {owner.public_institution?.name || "N/A"}
                  </p>
                </>
              )}
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center mb-3">
                <EnvironmentOutlined className="text-xl text-red-600 mr-2" />
                <Text strong className="text-lg">
                  Coordonnées
                </Text>
              </div>
              <p>
                <strong>Email:</strong> {owner.email || "N/A"}
              </p>
              <p>
                <strong>Téléphone Principal:</strong> {owner.phone || "N/A"}
              </p>
              <p>
                <strong>Adresse 1:</strong> {owner.address || "N/A"}
              </p>
              <p>
                <strong>Adresse 2:</strong> {owner.address2 || "N/A"}
              </p>
              <p>
                <strong>Ville:</strong> {owner.city || "N/A"}
              </p>
              <p>
                <strong>Code Postal:</strong> {owner.postal_code || "N/A"}
              </p>
              <p>
                <strong>Pays:</strong> {owner.country || "N/A"}
              </p>
            </div>
            {owner.type === "companies" && (
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center mb-3">
                  <PhoneOutlined className="text-xl text-green-600 mr-2" />
                  <Text strong className="text-lg">
                    Contact Personnel
                  </Text>
                </div>
                <p>
                  <strong>Prenom:</strong> {owner.contact_first_name || "N/A"}
                </p>
                <p>
                  <strong>Nom:</strong> {owner.contact_last_name || "N/A"}
                </p>
                <p>
                  <strong>Téléphone:</strong> {owner.contact_phone || "N/A"}
                </p>
              </div>
            )}
          </div>
        </div>

        <Descriptions
          bordered
          column={{ xxl: 3, xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
          className="bg-white rounded-lg shadow-sm"
          labelStyle={{ fontWeight: "600" }}
          title={
            <div className="flex items-center my-2">
              <CalendarOutlined className="text-xl text-blue-600 mr-2" />
              <Text strong className="text-lg">
                Informations système
              </Text>
            </div>
          }
        >
          <Descriptions.Item label="Date d'enregistrement">
            {owner.created_at
              ? dayjs(owner.created_at).format("DD/MM/YYYY HH:mm")
              : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Dernière mise à jour">
            {owner.updated_at
              ? dayjs(owner.updated_at).format("DD/MM/YYYY HH:mm")
              : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Identifiant Client">
            #{owner.id}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
};

export default VehicleOwnerTab;
